/**
 * Payment API Contract Schemas
 *
 * These schemas define the contract between backend and frontend for payment operations.
 * Use these schemas for request validation and type inference.
 *
 * @example
 * ```typescript
 * import { InitiatePaymentSchema, type InitiatePaymentRequest } from '@lov/shared';
 * ```
 */

import { z } from 'zod';

// ============================================
// PAYMENT STATUS
// ============================================

/**
 * Payment status enum (matches Prisma PaymentStatus)
 */
export const PaymentStatusEnum = z.enum([
  'PENDING',
  'COMPLETED',
  'FAILED',
  'REFUNDED',
  'CANCELLED',
  'PARTIAL_REFUND',
  'EXPIRED',
]);
export type PaymentStatusValue = z.infer<typeof PaymentStatusEnum>;

// ============================================
// PAYMENT INITIATION
// ============================================

/**
 * Initiate payment request schema
 *
 * Used to start payment flow for a booking
 */
export const InitiatePaymentSchema = z.object({
  bookingId: z.string().uuid('Invalid booking ID'),
});
export type InitiatePaymentRequest = z.infer<typeof InitiatePaymentSchema>;

/**
 * Payment intent response from initiate endpoint
 */
export const PaymentIntentResponseSchema = z.object({
  id: z.string(),
  clientSecret: z.string(),
  amount: z.number(),
  currency: z.string(),
  status: z.string(),
  redirectData: z
    .object({
      url: z.string(),
      params: z.record(z.string()),
    })
    .optional(),
});
export type PaymentIntentResponse = z.infer<typeof PaymentIntentResponseSchema>;

// ============================================
// PAYMENT STATUS
// ============================================

/**
 * Payment status response from status check endpoint
 */
export const PaymentStatusResponseSchema = z.object({
  status: z.enum(['pending', 'success', 'failed']),
  amount: z.number(),
  transactionId: z.string().optional(),
  message: z.string().optional(),
});
export type PaymentStatusResponse = z.infer<typeof PaymentStatusResponseSchema>;

// ============================================
// REFUNDS
// ============================================

/**
 * Refund payment request schema
 *
 * Used to initiate a refund for a completed payment.
 * If amount is not provided, full refund is processed.
 */
export const RefundPaymentSchema = z.object({
  amount: z.number().positive('Amount must be positive').optional(),
  reason: z
    .string()
    .min(10, 'Reason must be at least 10 characters')
    .max(500, 'Reason must not exceed 500 characters'),
});
export type RefundPaymentRequest = z.infer<typeof RefundPaymentSchema>;

/**
 * Mark refund as processed (manual refund tracking)
 */
export const MarkRefundProcessedSchema = z.object({
  refundTransactionId: z.string().optional(),
  notes: z.string().max(1000).optional(),
});
export type MarkRefundProcessedRequest = z.infer<typeof MarkRefundProcessedSchema>;

/**
 * Create manual refund (admin only - for ICICI refunds processed outside system)
 */
export const CreateManualRefundSchema = z.object({
  paymentId: z.string().uuid('Invalid payment ID'),
  amount: z.number().positive('Amount must be positive'),
  reason: z
    .string()
    .min(10, 'Reason must be at least 10 characters')
    .max(500, 'Reason must not exceed 500 characters'),
  manualRefundNotes: z.string().max(1000, 'Notes must not exceed 1000 characters').optional(),
  manualRefundDate: z.string().datetime('Invalid datetime format').or(z.date()),
  transactionId: z.string().optional(),
});
export type CreateManualRefundRequest = z.infer<typeof CreateManualRefundSchema>;

/**
 * Refund response schema (includes manual refund fields)
 */
export const RefundResponseSchema = z.object({
  id: z.string().uuid(),
  paymentId: z.string().uuid(),
  amount: z.number(),
  reason: z.string(),
  initiatedBy: z.string(),
  status: z.enum(['PENDING', 'COMPLETED', 'FAILED', 'CANCELLED']),
  refundMethod: z.string().nullish(),
  transactionId: z.string().nullish(),
  processedAt: z.string().or(z.date()).nullish(),
  failureReason: z.string().nullish(),
  // Manual refund tracking
  manualRefund: z.boolean(),
  manualRefundNotes: z.string().nullish(),
  manualRefundDate: z.string().or(z.date()).nullish(),
  manualRefundBy: z.string().nullish(),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
});
export type RefundResponse = z.infer<typeof RefundResponseSchema>;

// ============================================
// PAYMENT RESPONSE TYPES
// ============================================

/**
 * Payment response with booking details
 */
export const PaymentResponseSchema = z.object({
  id: z.string(),
  bookingId: z.string().uuid(),
  amount: z.number(),
  currency: z.string(),
  status: PaymentStatusEnum,
  paymentMethod: z.string().optional(),
  transactionId: z.string().optional(),
  paidAt: z.string().or(z.date()).nullish(),
  refundAmount: z.number().nullish(),
  refundedAt: z.string().or(z.date()).nullish(),
  refundReason: z.string().nullish(),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
});
export type PaymentResponse = z.infer<typeof PaymentResponseSchema>;

// ============================================
// PAYER DETAILS
// ============================================

/**
 * Payer details captured from payment webhooks
 */
export const PayerDetailsSchema = z.object({
  id: z.string().uuid(),
  paymentId: z.string(),
  payerName: z.string().nullish(),
  payerMobile: z.string().nullish(),
  payerEmail: z.string().nullish(),
  payerVPA: z.string().nullish(),
  bankRRN: z.string().nullish(),
  trackingId: z.string().nullish(),
  authCode: z.string().nullish(),
  paymentMode: z.string().nullish(),
  cardType: z.string().nullish(),
  cardLastFour: z.string().nullish(),
  bankName: z.string().nullish(),
  walletName: z.string().nullish(),
  txnInitDate: z.string().or(z.date()).nullish(),
  txnCompletionDate: z.string().or(z.date()).nullish(),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
});
export type PayerDetails = z.infer<typeof PayerDetailsSchema>;

/**
 * ICICI status polling response
 */
export const ICICIStatusResponseSchema = z.object({
  status: z.enum(['pending', 'success', 'failed']),
  amount: z.number(),
  transactionId: z.string().optional(),
  message: z.string().optional(),
  payerDetails: PayerDetailsSchema.optional(),
});
export type ICICIStatusResponse = z.infer<typeof ICICIStatusResponseSchema>;

// ============================================
// UPI PAYMENTS (EAZYPAY)
// ============================================

/**
 * UPI payment methods supported by EazyPay
 */
export const UpiPaymentMethodEnum = z.enum(['qr', 'intent', 'collect']);
export type UpiPaymentMethod = z.infer<typeof UpiPaymentMethodEnum>;

/**
 * Initiate UPI payment request
 *
 * Used to start UPI payment flow (QR, Intent, or Collect Pay)
 */
export const InitiateUpiPaymentSchema = z.object({
  bookingId: z.string().uuid('Invalid booking ID'),
  method: UpiPaymentMethodEnum,
  payerVpa: z
    .string()
    .regex(/^[\w.-]+@[\w]+$/, 'Invalid UPI ID format')
    .optional(), // Required for collect method
});
export type InitiateUpiPaymentRequest = z.infer<typeof InitiateUpiPaymentSchema>;

/**
 * UPI payment initiation response
 *
 * Contains QR string, intent URL, or collect pay reference
 */
export const UpiPaymentResponseSchema = z.object({
  paymentId: z.string().uuid(),
  method: UpiPaymentMethodEnum,
  qrString: z.string().optional(), // For QR method
  intentUrl: z.string().url().optional(), // For intent method
  refId: z.string(), // EazyPay reference ID
  merchantTranId: z.string(), // Transaction ID
  amount: z.number().positive(),
  currency: z.string().default('INR'),
  expiresAt: z.string().datetime(), // ISO datetime when payment expires
  status: z.enum(['initiated', 'pending', 'success', 'failed', 'expired']),
});
export type UpiPaymentResponse = z.infer<typeof UpiPaymentResponseSchema>;

/**
 * Collect Pay request (VPA-based UPI payment)
 *
 * Sends payment request to customer's UPI ID
 */
export const CollectPayRequestSchema = z.object({
  bookingId: z.string().uuid('Invalid booking ID'),
  payerVpa: z.string().regex(/^[\w.-]+@[\w]+$/, 'Invalid UPI ID format'),
});
export type CollectPayRequest = z.infer<typeof CollectPayRequestSchema>;

/**
 * EazyPay callback payload (decrypted)
 *
 * Received from EazyPay webhook after payment completion
 */
export const EazypayCallbackSchema = z.object({
  merchantId: z.string(),
  subMerchantId: z.string(),
  terminalId: z.string(),
  BankRRN: z.string(), // Bank Reference Number
  merchantTranId: z.string(),
  PayerName: z.string().optional(),
  PayerMobile: z.string().optional(),
  PayerVA: z.string().optional(), // Virtual Address (UPI ID)
  PayerAmount: z.string(), // Amount as string
  TxnStatus: z.enum(['SUCCESS', 'FAILURE', 'PENDING', 'INITIATED', 'EXPIRED']),
  TxnInitDate: z.string(), // ISO date string
  TxnCompletionDate: z.string().optional(), // ISO date string
});
export type EazypayCallback = z.infer<typeof EazypayCallbackSchema>;

/**
 * UPI payment status response
 *
 * Used for polling payment status
 */
export const UpiPaymentStatusResponseSchema = z.object({
  status: z.enum(['pending', 'success', 'failed', 'expired']),
  amount: z.number(),
  transactionId: z.string(),
  merchantTranId: z.string(),
  bankRrn: z.string().optional(),
  payerVpa: z.string().optional(),
  payerName: z.string().optional(),
  payerMobile: z.string().optional(),
  txnDate: z.string().datetime().optional(),
  message: z.string().optional(),
});
export type UpiPaymentStatusResponse = z.infer<typeof UpiPaymentStatusResponseSchema>;
